import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { QuotationForm } from './QuotationForm';
import { QuotationHeader } from './components/QuotationHeader';
import { VolumeOverview } from './components/VolumeOverview';
import { FeeStructureTable } from './components/FeeStructureTable';
import { RevenueBreakdown } from './components/RevenueBreakdown';

interface Quotation {
  id: string;
  name: string;
  created_at: string;
  bank: {
    name: string;
    alias: string;
  };
  bank_fees: {
    bank_id: string;
    bank_name: string;
    bank_alias: string;
    fees: BankFee[];
  }[];
  estimated_payments: number;
  estimated_payouts: number;
  average_amount: number;
  payment_approved_fixed_fee: number;
  payment_approved_percentage_fee: number;
  payment_approved_max_cap: number;
  payment_declined_fixed_fee: number;
  payment_declined_percentage_fee: number;
  payment_declined_max_cap: number;
  payment_returned_fixed_fee: number;
  payment_returned_percentage_fee: number;
  payment_returned_max_cap: number;
  payout_approved_fixed_fee: number;
  payout_approved_percentage_fee: number;
  payout_approved_max_cap: number;
  payout_declined_fixed_fee: number;
  payout_declined_percentage_fee: number;
  payout_declined_max_cap: number;
  payout_returned_fixed_fee: number;
  payout_returned_percentage_fee: number;
  payout_returned_max_cap: number;
}

import { BankFee } from '../../types';

export function QuotationDetails() {
  const { quotationId } = useParams();
  const [quotation, setQuotation] = useState<Quotation | null>(null);
  const [bankFees, setBankFees] = useState<BankFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditForm, setShowEditForm] = useState(false);

  useEffect(() => {
    loadQuotationData();
  }, [quotationId]);

  const loadQuotationData = async () => {
    try {
      setLoading(true);
      const { data: quotationData, error: quotationError } = await supabase
        .rpc('get_quotation_details', { quotation_id_param: quotationId });

      if (quotationError) throw quotationError;
      if (!quotationData) throw new Error('Quotation not found');

      setQuotation(quotationData);
      setBankFees(quotationData.bank_fees || []);
    } catch (err) {
      console.error('Error loading quotation:', err);
      setError(err instanceof Error ? err.message : 'Failed to load quotation');
    } finally {
      setLoading(false);
    }
  };

  const calculateFeeBreakdown = (
    amount: number,
    count: number,
    approvedFixedFee: number,
    approvedPercentageFee: number,
    approvedMaxCap: number,
    bankFixedFee: number,
    bankPercentageFee: number,
    bankMaxCap: number
  ) => {
    const totalFixed = approvedFixedFee * count;
    const totalPercentage = (amount * approvedPercentageFee / 100) * count;
    const totalBeforeCap = totalFixed + totalPercentage;
    const totalAfterCap = approvedMaxCap > 0 ? Math.min(totalBeforeCap, approvedMaxCap * count) : totalBeforeCap;

    const bankTotalFixed = bankFixedFee * count;
    const bankTotalPercentage = (amount * bankPercentageFee / 100) * count;
    const bankTotalBeforeCap = bankTotalFixed + bankTotalPercentage;
    const bankTotalAfterCap = bankMaxCap > 0 ? Math.min(bankTotalBeforeCap, bankMaxCap * count) : bankTotalBeforeCap;

    return {
      merchant: {
        fixed: totalFixed,
        percentage: totalPercentage,
        beforeCap: totalBeforeCap,
        afterCap: totalAfterCap,
        capApplied: approvedMaxCap > 0 && totalBeforeCap > (approvedMaxCap * count)
      },
      bank: {
        fixed: bankTotalFixed,
        percentage: bankTotalPercentage,
        beforeCap: bankTotalBeforeCap,
        afterCap: bankTotalAfterCap,
        capApplied: bankMaxCap > 0 && bankTotalBeforeCap > (bankMaxCap * count)
      },
      profit: totalAfterCap - bankTotalAfterCap
    };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error || !quotation) {
    return (
      <div className="text-center py-12">
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-[14px] text-red-600">{error || 'Quotation not found'}</p>
        </div>
      </div>
    );
  }

  const bankPaymentFees = bankFees.find(
    bankFeeGroup => bankFeeGroup.bank_id === quotation.bank?.id
  )?.fees.find(
    f => f.type === 'payment' &&
         f.payment_method === 'etransfer_request_money' &&
         f.status === 'approved'
  ) || { per_transaction_fee: 0, rate_fee_percentage: 0, max_fee_cap: 0 };

  const bankPayoutFees = bankFees.find(
    bankFeeGroup => bankFeeGroup.bank_id === quotation.bank?.id
  )?.fees.find(
    f => f.type === 'payout' &&
         f.payment_method === 'etransfer_send_money' &&
         f.status === 'approved'
  ) || { per_transaction_fee: 0, rate_fee_percentage: 0, max_fee_cap: 0 };

  const paymentBreakdown = calculateFeeBreakdown(
    quotation.average_amount,
    quotation.estimated_payments,
    quotation.payment_approved_fixed_fee,
    quotation.payment_approved_percentage_fee,
    quotation.payment_approved_max_cap,
    bankPaymentFees.per_transaction_fee,
    bankPaymentFees.rate_fee_percentage,
    bankPaymentFees.max_fee_cap
  );

  const payoutBreakdown = calculateFeeBreakdown(
    quotation.average_amount,
    quotation.estimated_payouts,
    quotation.payout_approved_fixed_fee,
    quotation.payout_approved_percentage_fee,
    quotation.payout_approved_max_cap,
    bankPayoutFees.per_transaction_fee,
    bankPayoutFees.rate_fee_percentage,
    bankPayoutFees.max_fee_cap
  );

  const totalRevenue = paymentBreakdown.merchant.afterCap + payoutBreakdown.merchant.afterCap;
  const totalBankFees = paymentBreakdown.bank.afterCap + payoutBreakdown.bank.afterCap;
  const totalProfit = paymentBreakdown.profit + payoutBreakdown.profit;

  return (
    <div className="space-y-6">
      <QuotationHeader
        name={quotation.name}
        bankName={quotation.bank?.name}
        bankAlias={quotation.bank?.alias}
        createdAt={quotation.created_at}
        onEdit={() => setShowEditForm(true)}
      />

      <VolumeOverview
        estimatedPayments={quotation.estimated_payments}
        estimatedPayouts={quotation.estimated_payouts}
        averageAmount={quotation.average_amount}
        totalRevenue={totalRevenue}
        totalBankFees={totalBankFees}
        totalProfit={totalProfit}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <RevenueBreakdown
          title="Payment Revenue"
          breakdown={paymentBreakdown}
          transactionCount={quotation.estimated_payments}
          fixedFee={quotation.payment_approved_fixed_fee}
          percentageFee={quotation.payment_approved_percentage_fee}
          maxCap={quotation.payment_approved_max_cap}
          bankFixedFee={bankPaymentFees.per_transaction_fee}
          bankPercentageFee={bankPaymentFees.rate_fee_percentage}
          bankMaxCap={bankPaymentFees.max_fee_cap}
        />

        <RevenueBreakdown
          title="Payout Revenue"
          breakdown={payoutBreakdown}
          transactionCount={quotation.estimated_payouts}
          fixedFee={quotation.payout_approved_fixed_fee}
          percentageFee={quotation.payout_approved_percentage_fee}
          maxCap={quotation.payout_approved_max_cap}
          bankFixedFee={bankPayoutFees.per_transaction_fee}
          bankPercentageFee={bankPayoutFees.rate_fee_percentage}
          bankMaxCap={bankPayoutFees.max_fee_cap}
        />
      </div>

      <div className="space-y-4">
        <h2 className="section-title">Fee Structure Comparison</h2>
        <FeeStructureTable
          title="Payment Fees (eTransfer Request Money)"
          merchantFees={{
            fixedFee: quotation.payment_approved_fixed_fee,
            percentageFee: quotation.payment_approved_percentage_fee,
            maxCap: quotation.payment_approved_max_cap
          }}
          bankFees={{
            fixedFee: bankPaymentFees.per_transaction_fee,
            percentageFee: bankPaymentFees.rate_fee_percentage,
            maxCap: bankPaymentFees.max_fee_cap
          }}
        />
        
        <FeeStructureTable
          title="Payout Fees (eTransfer Send Money)"
          merchantFees={{
            fixedFee: quotation.payout_approved_fixed_fee,
            percentageFee: quotation.payout_approved_percentage_fee,
            maxCap: quotation.payout_approved_max_cap
          }}
          bankFees={{
            fixedFee: bankPayoutFees.per_transaction_fee,
            percentageFee: bankPayoutFees.rate_fee_percentage,
            maxCap: bankPayoutFees.max_fee_cap
          }}
        />
      </div>

      {showEditForm && (
        <QuotationForm
          initialData={{
            id: quotation.id,
            name: quotation.name,
            bank_id: quotation.bank.id,
            estimated_payments: quotation.estimated_payments,
            estimated_payouts: quotation.estimated_payouts,
            average_amount: quotation.average_amount,
            payment_approved_fixed_fee: quotation.payment_approved_fixed_fee,
            payment_approved_percentage_fee: quotation.payment_approved_percentage_fee,
            payment_approved_max_cap: quotation.payment_approved_max_cap,
            payment_declined_fixed_fee: quotation.payment_declined_fixed_fee,
            payment_declined_percentage_fee: quotation.payment_declined_percentage_fee,
            payment_declined_max_cap: quotation.payment_declined_max_cap,
            payment_returned_fixed_fee: quotation.payment_returned_fixed_fee,
            payment_returned_percentage_fee: quotation.payment_returned_percentage_fee,
            payment_returned_max_cap: quotation.payment_returned_max_cap,
            payout_approved_fixed_fee: quotation.payout_approved_fixed_fee,
            payout_approved_percentage_fee: quotation.payout_approved_percentage_fee,
            payout_approved_max_cap: quotation.payout_approved_max_cap,
            payout_declined_fixed_fee: quotation.payout_declined_fixed_fee,
            payout_declined_percentage_fee: quotation.payout_declined_percentage_fee,
            payout_declined_max_cap: quotation.payout_declined_max_cap,
            payout_returned_fixed_fee: quotation.payout_returned_fixed_fee,
            payout_returned_percentage_fee: quotation.payout_returned_percentage_fee,
            payout_returned_max_cap: quotation.payout_returned_max_cap
          }}
          onClose={() => setShowEditForm(false)}
          onSuccess={loadQuotationData}
        />
      )}
    </div>
  );
}
